import { packagesApi } from "@/modules/packages/presentation/api/client";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
    CreateStudentDTO,
    UpdateStudentDTO,
    UploadMembershipSignedDocumentDTO,
} from "../../domain/contracts";
import type { UpdatePackageSelectedClassesDTO } from "../../domain/contracts/update-package-selected-classes";
import { studentsApi } from "./client";

// Query keys
export const studentsKeys = {
  all: ["students"] as const,
  lists: () => [...studentsKeys.all, "list"] as const,
  list: (query: OffsetPageQueryDTO) =>
    [...studentsKeys.lists(), query] as const,
  details: () => [...studentsKeys.all, "detail"] as const,
  detail: (id: string) => [...studentsKeys.details(), id] as const,
};

// List query
export function useStudentsQuery(query: OffsetPageQueryDTO) {
  return useQuery({
    queryKey: studentsKeys.list(query),
    queryFn: () => studentsApi.list(query),
  });
}

// Get by ID query
export function useStudentQuery(id: string) {
  return useQuery({
    queryKey: studentsKeys.detail(id),
    queryFn: () => studentsApi.get(id),
    enabled: !!id,
  });
}

// Get summary query
export function useStudentSummaryQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "summary"],
    queryFn: () => studentsApi.getSummary(id),
    enabled: !!id,
  });
}

// Get active packages query (FIFO list)
export function useStudentActivePackagesQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "active-packages"],
    queryFn: () => studentsApi.listActivePackages(id),
    enabled: !!id,
  });
}

// Get active package query (deprecated — first FIFO)
export function useStudentActivePackageQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "active-package"],
    queryFn: () => studentsApi.getActivePackage(id),
    enabled: !!id,
  });
}

// Update package class ຊົ່ວໂມງຮຽນ mutation
export function useUpdatePackageClassSessions(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof studentsApi.updatePackageClassSessions>[1]) =>
      studentsApi.updatePackageClassSessions(studentId, input),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...studentsKeys.detail(studentId), "active-packages"],
      });
      qc.invalidateQueries({ queryKey: [...studentsKeys.detail(studentId), "active-package"] });
      qc.invalidateQueries({ queryKey: studentsKeys.detail(studentId) });
    },
  });
}

// Cancel student (class) package mutation
export function useCancelStudentPackage(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { packageId: string; cancelReason: string }) =>
      packagesApi.studentPackages.cancel(input.packageId, {
        cancelReason: input.cancelReason,
      }),
    onSuccess: (_, __, context) => {
      toast.success("ຍົກເລີກແພັກເກດສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentsKeys.detail(studentId) });
      qc.invalidateQueries({
        queryKey: [...studentsKeys.detail(studentId), "active-packages"],
      });
      qc.invalidateQueries({
        queryKey: [...studentsKeys.detail(studentId), "active-package"],
      });
    },
    onError: () => {
      toast.error("ຍົກເລີກແພັກເກດລົ້ມເຫຼວ");
    },
  });
}

// Update selected template rules + ຊົ່ວໂມງຮຽນ split (maxSelectableClasses)
export function useUpdateStudentPackageSelectedClasses(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      packageId,
      ...body
    }: { packageId: string } & UpdatePackageSelectedClassesDTO) =>
      studentsApi.updatePackageSelectedClasses(studentId, packageId, body),
    onSuccess: () => {
      toast.success("ອັບເດດຄລາດສ໌ສຳເລັດ");
      qc.invalidateQueries({
        queryKey: [...studentsKeys.detail(studentId), "active-packages"],
      });
      qc.invalidateQueries({
        queryKey: [...studentsKeys.detail(studentId), "active-package"],
      });
      qc.invalidateQueries({ queryKey: studentsKeys.detail(studentId) });
    },
    onError: (e) =>
      toast.error(
        e instanceof Error ? e.message : "ອັບເດດຄລາດສ໌ບໍ່ສຳເລັດ",
      ),
  });
}

// Get packages query
export function useStudentPackagesQuery(
  id: string,
  query: Parameters<typeof studentsApi.getPackages>[1],
) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "packages", query],
    queryFn: () => studentsApi.getPackages(id, query),
    enabled: !!id,
  });
}

// Get attendance summary query
export function useStudentAttendanceSummaryQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "attendance-summary"],
    queryFn: () => studentsApi.getAttendanceSummary(id),
    enabled: !!id,
  });
}

// Get attendance query
export function useStudentAttendanceQuery(
  id: string,
  query: Parameters<typeof studentsApi.getAttendance>[1],
) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "attendance", query],
    queryFn: () => studentsApi.getAttendance(id, query),
    enabled: !!id,
  });
}

// Get active private lesson packages query (FIFO list)
export function useStudentActivePrivateLessonPackagesQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "active-private-lesson-packages"],
    queryFn: () => studentsApi.listActivePrivateLessonPackages(id),
    enabled: !!id,
  });
}

// Get active private lesson package query (deprecated — first FIFO)
export function useStudentActivePrivateLessonPackageQuery(id: string) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "active-private-lesson-package"],
    queryFn: () => studentsApi.getActivePrivateLessonPackage(id),
    enabled: !!id,
  });
}

// Get private lesson packages query
export function useStudentPrivateLessonPackagesQuery(
  id: string,
  query: Parameters<typeof studentsApi.listPrivateLessonPackages>[1],
) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "private-lesson-packages", query],
    queryFn: () => studentsApi.listPrivateLessonPackages(id, query),
    enabled: !!id,
  });
}

// Get private lesson bookings query
export function useStudentPrivateLessonBookingsQuery(
  id: string,
  query: Parameters<typeof studentsApi.listPrivateLessonBookings>[1],
) {
  return useQuery({
    queryKey: [...studentsKeys.detail(id), "private-lesson-bookings", query],
    queryFn: () => studentsApi.listPrivateLessonBookings(id, query),
    enabled: !!id,
  });
}

// Create mutation
export function useCreateStudent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStudentDTO) => studentsApi.create(input),
    onSuccess: () => {
      toast.success("ສ້າງນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentsKeys.all });
    },
    onError: () => {
      toast.error("ສ້າງນັກຮຽນລົ້ມເຫຼວ");
    },
  });
}

// Update mutation
export function useUpdateStudent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStudentDTO) => studentsApi.update(id, input),
    onSuccess: () => {
      toast.success("ອັບເດດນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentsKeys.all });
      qc.invalidateQueries({ queryKey: studentsKeys.detail(id) });
    },
    onError: () => {
      toast.error("ອັບເດດນັກຮຽນລົ້ມເຫຼວ");
    },
  });
}

export function useSetStudentMembershipSignedDocument(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadMembershipSignedDocumentDTO) =>
      studentsApi.setMembershipSignedDocument(id, input),
    onSuccess: () => {
      toast.success("ບັນທຶກເອກະສານສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentsKeys.detail(id) });
      qc.invalidateQueries({ queryKey: studentsKeys.all });
    },
    onError: () => {
      toast.error("ບັນທຶກເອກະສານລົ້ມເຫຼວ");
    },
  });
}

// Delete mutation
export function useDeleteStudent() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => studentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentsKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Toggle status mutation
export function useToggleStudentStatus(id: string) {
  const qc = useQueryClient();
  const { data: student } = useStudentQuery(id);

  return useMutation({
    mutationFn: async () => {
      if (!student) throw new Error("Student not found");
      const newStatus = student.status === "active" ? "deactivated" : "active";
      return studentsApi.update(id, { status: newStatus });
    },
    onSuccess: () => {
      toast.success("ປ່ຽນສະຖານະນັກຮຽນສຳເລັດ");
      qc.invalidateQueries({ queryKey: studentsKeys.all });
      qc.invalidateQueries({ queryKey: studentsKeys.detail(id) });
    },
    onError: () => {
      toast.error("ປ່ຽນສະຖານະນັກຮຽນລົ້ມເຫຼວ");
    },
  });
}

// Bulk delete mutation
export function useBulkDeleteStudents() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (ids: string[]) => studentsApi.bulkDelete({ ids }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentsKeys.all });
    },
  });

  const run = (ids: string[]) =>
    new Promise<{ deletedCount: number }>((resolve, reject) => {
      base.mutate(ids, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// Bulk toggle status mutation
export function useBulkToggleStudentStatus() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (input: { ids: string[]; status: "active" | "deactivated" }) =>
      studentsApi.bulkToggleStatus(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: studentsKeys.all });
    },
  });

  const run = (input: { ids: string[]; status: "active" | "deactivated" }) =>
    new Promise<{ updatedCount: number }>((resolve, reject) => {
      base.mutate(input, {
        onSuccess: (result) => resolve(result),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
